import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type {
  PlatformHealthPulseResponse,
  PulseOperationStatus,
  PulseOperationView,
} from '@/pages/platform-health/types';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  GitBranch,
  RefreshCw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

const REFRESH_INTERVAL_MS = 60_000;
const RUNTIMES = ['all', 'firebase_schedule', 'github_actions', 'cloudflare_cron', 'cloud_tasks'];
const STATUSES = [
  'all',
  'never_ran',
  'running',
  'stuck',
  'failed',
  'partial',
  'recovered',
  'exempt',
  'healthy',
  'pending',
  'unknown',
];
const MIGRATION_STATES = ['all', 'native', 'migration_debt', 'exempt'];

function display(value: string) {
  return value.replace(/_/g, ' ');
}

function timestamp(value: string | null) {
  return value ? new Date(value).toLocaleString() : 'Not observed';
}

function statusTone(status: PulseOperationStatus) {
  if (['failed', 'stuck', 'partial', 'never_ran'].includes(status)) return 'red';
  if (['pending', 'unknown'].includes(status)) return 'amber';
  if (status === 'running') return 'blue';
  if (status === 'exempt') return 'neutral';
  return 'green';
}

function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: string }) {
  const colors: Record<string, string> = {
    neutral: 'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-gray-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
    green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  };
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        colors[tone] ?? colors.neutral
      )}
    >
      {children}
    </span>
  );
}

function Metric({
  label,
  value,
  warning = false,
}: {
  label: string;
  value: number;
  warning?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800">
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums text-gray-950 dark:text-white',
          warning && value > 0 && 'text-red-700 dark:text-red-300'
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function OperationDetail({ operation }: { operation: PulseOperationView }) {
  const accounting = operation.accounting;
  const runsUrl = `/admin/platform-health/test-runs?subsystem=pulse&search=${encodeURIComponent(
    operation.latestRunId ?? operation.operationId
  )}${operation.latestRunId ? `&run=${encodeURIComponent(`run:${operation.latestRunId}`)}` : ''}`;
  return (
    <div
      data-testid="pulse-operation-detail"
      className="border-t border-gray-100 bg-gray-50/70 px-4 py-4 dark:border-zinc-700 dark:bg-zinc-900/40"
    >
      <p className="text-sm text-gray-700 dark:text-gray-200">{operation.explanation}</p>
      <dl className="mt-4 grid gap-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className="text-gray-500">Last expected</dt>
          <dd className="mt-0.5 font-medium">{timestamp(operation.lastExpectedAt)}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Next expected</dt>
          <dd className="mt-0.5 font-medium">{timestamp(operation.nextExpectedAt)}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Last observed</dt>
          <dd className="mt-0.5 font-medium">{timestamp(operation.lastObservedAt)}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Attempt / retries</dt>
          <dd className="mt-0.5 font-medium">
            {operation.attemptNumber ?? 'not observed'} / {operation.retryCount}
          </dd>
        </div>
      </dl>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <section className="rounded border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
            <GitBranch className="h-3.5 w-3.5" aria-hidden="true" /> Dispatch graph
          </h3>
          <dl className="mt-2 space-y-2 text-xs">
            <div>
              <dt className="text-gray-500">Parent</dt>
              <dd className="break-all font-mono">
                {operation.parentOperationId ?? 'standalone/root'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Children</dt>
              <dd className="break-all font-mono">
                {operation.childOperationIds.join(', ') || 'none'}
              </dd>
            </div>
          </dl>
        </section>
        <section className="rounded border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
            Item accounting
          </h3>
          {accounting ? (
            <dl className="mt-2 grid grid-cols-3 gap-2 text-xs sm:grid-cols-5">
              {[
                ['Expected', accounting.expected ?? 'runtime'],
                ['Processed', accounting.processed],
                ['Failed', accounting.failed],
                ['Skipped', accounting.skipped],
                ['Deferred', accounting.deferred],
              ].map(([label, value]) => (
                <div key={String(label)}>
                  <dt className="text-gray-500">{label}</dt>
                  <dd className="font-semibold tabular-nums">{value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
              No native accounting evidence has been observed.
            </p>
          )}
          {accounting?.childDispatches.length ? (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
              Child dispatches:{' '}
              {accounting.childDispatches
                .map((child) => `${child.operationId} × ${child.count}`)
                .join(', ')}
            </p>
          ) : null}
        </section>
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <section
          data-testid="pulse-reliability"
          className="rounded border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800"
        >
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
            Reliability trend
          </h3>
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
            {operation.reliability.completed} completed · {operation.reliability.failed} failed ·{' '}
            {operation.reliability.active} active across {operation.reliability.runs} runs · success{' '}
            {operation.reliability.successRate === null
              ? 'not established'
              : `${Math.round(operation.reliability.successRate * 100)}%`}
          </p>
          {operation.reliability.trend.length > 0 ? (
            <ul className="mt-2 space-y-1 font-mono text-[11px] text-gray-600 dark:text-gray-300">
              {operation.reliability.trend.map((bucket) => (
                <li key={bucket.date}>
                  {bucket.date}: {bucket.completed} complete / {bucket.failed} failed /{' '}
                  {bucket.active} active
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
              No run history has been observed.
            </p>
          )}
        </section>
        <section
          data-testid="pulse-invocations"
          className="rounded border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800"
        >
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
            Recent invocations
          </h3>
          {operation.invocations.length > 0 ? (
            <ul className="mt-2 space-y-2 text-xs text-gray-600 dark:text-gray-300">
              {operation.invocations.map((invocation) => (
                <li
                  key={invocation.invocationId}
                  className="rounded bg-gray-50 p-2 dark:bg-zinc-900"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={invocation.stuck ? 'red' : statusTone(operation.status)}>
                      {invocation.stuck ? 'stuck' : display(invocation.lifecycle)}
                    </Badge>
                    <code className="break-all text-[10px]">{invocation.runId}</code>
                  </div>
                  <p className="mt-1">
                    {timestamp(invocation.observedAt)} · attempt{' '}
                    {invocation.attemptNumber ?? 'unknown'}
                    {invocation.isRetry ? ' (retry)' : ''} · processed{' '}
                    {invocation.accounting.processed}/{invocation.accounting.expected ?? '?'}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
              No invocation evidence has been observed.
            </p>
          )}
        </section>
      </div>
      {operation.exemption ? (
        <div className="mt-3 rounded border border-gray-300 bg-white p-3 text-xs dark:border-zinc-700 dark:bg-zinc-800">
          Exempt until {timestamp(operation.exemption.expiresAt)} — {operation.exemption.reason} (
          {operation.exemption.owner.displayName})
        </div>
      ) : null}
      {operation.classifications.length > 0 || operation.findings.length > 0 ? (
        <section className="mt-4">
          <h3 className="text-xs font-semibold text-gray-950 dark:text-white">Findings and gaps</h3>
          <ul className="mt-2 space-y-1 text-xs text-gray-700 dark:text-gray-200">
            {operation.classifications.map((classification) => (
              <li key={`${classification.code}:${classification.summary}`}>
                <strong>{display(classification.code)}:</strong> {classification.summary}
              </li>
            ))}
            {operation.findings.map((finding) => (
              <li key={finding.findingId}>
                <a
                  href={`/admin/platform-health/critical?finding=${encodeURIComponent(finding.findingId)}`}
                  className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  {finding.lifecycle} finding
                </a>{' '}
                — {finding.summary}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-3 text-xs">
        <a href={runsUrl} className="font-medium text-blue-600 hover:underline dark:text-blue-400">
          Open exact AXON run history
        </a>
        <a
          href={`https://github.com/ApptonomyInc/a7y/blob/main/${operation.source.path}`}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          Source: {operation.source.declarationKey}
        </a>
      </div>
      <p className="mt-3 break-all text-[10px] text-gray-500 dark:text-gray-400">
        Evidence inputs: {operation.evidenceRecordIds.join(', ') || 'none'}
      </p>
    </div>
  );
}

export function PulseOperationsContent({
  payload,
  selectedOperationId = null,
  onSelectOperation,
}: {
  payload: PlatformHealthPulseResponse;
  selectedOperationId?: string | null;
  onSelectOperation?: (operationId: string | null) => void;
}) {
  const { workbench } = payload;
  return (
    <div className="space-y-4">
      {!payload.available ? (
        <div
          data-testid="pulse-unavailable"
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100"
        >
          <strong>PULSE evidence is unavailable.</strong> Registry rows remain visible, but absence
          of evidence is not an all-clear.
        </div>
      ) : null}
      {payload.partial ? (
        <div
          data-testid="pulse-partial"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <strong>Partial or inconsistent evidence.</strong>
          <ul className="mt-2 list-disc pl-5 text-xs">
            {payload.gaps.map((gap, index) => (
              <li key={`${gap.code}:${index}`}>{gap.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <dl className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Metric label="Registry" value={workbench.coverage.denominator} />
        <Metric label="Evaluated" value={workbench.coverage.evaluated} />
        <Metric label="Healthy" value={workbench.coverage.healthy} />
        <Metric label="Running" value={workbench.statusCounts.running} />
        <Metric label="Failing" value={workbench.coverage.failing} warning />
        <Metric label="Never ran" value={workbench.statusCounts.never_ran} warning />
        <Metric label="Migration debt" value={workbench.migration.migration_debt} warning />
        <Metric label="Active findings" value={workbench.findings.active} warning />
      </dl>
      <div className="grid gap-3 lg:grid-cols-2">
        <section
          data-testid="pulse-evaluator-health"
          className={cn(
            'rounded-lg border p-4 text-sm',
            workbench.evaluator.status === 'healthy'
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100'
              : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100'
          )}
        >
          <div className="flex items-center gap-2 font-semibold">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Independent evaluator:{' '}
            {workbench.evaluator.status}
          </div>
          <p className="mt-1 text-xs">{workbench.evaluator.explanation}</p>
        </section>
        <section
          data-testid="pulse-axon-parity"
          className={cn(
            'rounded-lg border p-4 text-sm',
            payload.axonComparison.matches
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100'
              : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100'
          )}
        >
          <div className="flex items-center gap-2 font-semibold">
            <Activity className="h-4 w-4" aria-hidden="true" /> AXON parity:{' '}
            {payload.axonComparison.matches ? 'matched' : 'unverified or mismatched'}
          </div>
          <p className="mt-1 text-xs">
            AXON active findings {payload.axonComparison.axonActiveFindings ?? 'unknown'} · PULSE{' '}
            {payload.axonComparison.workbenchActiveFindings} · shared evidence{' '}
            {payload.axonComparison.sharedEvidenceRecordIds.length}
          </p>
        </section>
      </div>
      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
        <div className="border-b border-gray-200 px-4 py-3 text-xs text-gray-500 dark:border-zinc-700 dark:text-gray-400">
          Showing {workbench.filteredOperations} of {workbench.totalOperations} operations ·
          registry {workbench.registryVersion}
        </div>
        {workbench.operations.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">
            No operations match these filters.
          </p>
        ) : (
          workbench.operations.map((operation) => {
            const selected = selectedOperationId === operation.operationId;
            const Icon = ['healthy', 'recovered'].includes(operation.status)
              ? CheckCircle2
              : ['running', 'pending'].includes(operation.status)
                ? Activity
                : AlertTriangle;
            return (
              <details
                key={operation.operationId}
                data-testid="pulse-operation-row"
                data-operation-id={operation.operationId}
                open={selected || undefined}
                onToggle={(event) => {
                  if (!onSelectOperation) return;
                  const open = event.currentTarget.open;
                  if (open && !selected) onSelectOperation(operation.operationId);
                  else if (!open && selected) onSelectOperation(null);
                }}
                className="group border-b border-gray-200 last:border-b-0 dark:border-zinc-700"
              >
                <summary className="grid cursor-pointer list-none gap-3 px-4 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 sm:grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:items-center">
                  <Icon
                    className={cn(
                      'h-5 w-5',
                      statusTone(operation.status) === 'green'
                        ? 'text-emerald-600'
                        : statusTone(operation.status) === 'red'
                          ? 'text-red-600'
                          : statusTone(operation.status) === 'blue'
                            ? 'text-blue-600'
                            : 'text-amber-600'
                    )}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-gray-950 dark:text-white">
                        {operation.displayName}
                      </span>
                      <Badge tone={statusTone(operation.status)}>{display(operation.status)}</Badge>
                      <Badge>{display(operation.runtime)}</Badge>
                      <Badge tone={operation.instrumentationState === 'native' ? 'green' : 'amber'}>
                        {display(operation.instrumentationState)}
                      </Badge>
                    </div>
                    <p className="mt-1 truncate font-mono text-[11px] text-gray-500">
                      {operation.operationId}
                    </p>
                    <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                      <span>Last expected: {timestamp(operation.lastExpectedAt)}</span>
                      <span>Next expected: {timestamp(operation.nextExpectedAt)}</span>
                      <span>
                        Accounting:{' '}
                        {operation.accounting
                          ? `${operation.accounting.processed}/${operation.accounting.expected ?? '?'}`
                          : 'not observed'}
                      </span>
                      <span>Evidence gaps: {operation.evidenceGaps.length}</span>
                    </p>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    <p>{operation.owner.displayName}</p>
                    <p>
                      {operation.lastLifecycle
                        ? display(operation.lastLifecycle)
                        : 'never observed'}
                    </p>
                  </div>
                  <ChevronRight
                    className="h-4 w-4 text-gray-400 transition-transform group-open:rotate-90"
                    aria-hidden="true"
                  />
                </summary>
                <OperationDetail operation={operation} />
              </details>
            );
          })
        )}
      </section>
    </div>
  );
}

function SelectFilter({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: string;
  values: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="text-xs text-gray-600 dark:text-gray-300">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
      >
        {values.map((item) => (
          <option key={item} value={item}>
            {display(item)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function PulseOperationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const environment = searchParams.get('environment') ?? '';
  const runtime = searchParams.get('runtime') ?? 'all';
  const owner = searchParams.get('owner') ?? '';
  const family = searchParams.get('family') ?? '';
  const status = searchParams.get('status') ?? 'all';
  const migrationState = searchParams.get('migrationState') ?? 'all';
  const search = searchParams.get('search') ?? '';
  const selectedOperationId = searchParams.get('operation');
  const query = useQuery({
    queryKey: [
      'platform-health-pulse',
      environment,
      runtime,
      owner,
      family,
      status,
      migrationState,
      search,
    ],
    queryFn: () =>
      api.getPlatformHealthPulse({
        environment: environment || undefined,
        runtime: runtime === 'all' ? undefined : runtime,
        owner: owner || undefined,
        family: family || undefined,
        status: status === 'all' ? undefined : status,
        migrationState: migrationState === 'all' ? undefined : migrationState,
        search: search || undefined,
      }),
    refetchInterval: REFRESH_INTERVAL_MS,
    retry: false,
  });

  function setParam(name: string, value: string | null, defaultValue?: string) {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(name);
    else next.set(name, value);
    if (name !== 'operation') next.delete('operation');
    setSearchParams(next);
  }

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="PULSE Operations"
        subtitle="Schedule truth, invocation lifecycle, dispatch accounting, and migration coverage"
      />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {query.data?.data.workbench.generatedAt
            ? `Environment ${query.data.data.workbench.environment} · updated ${timestamp(query.data.data.workbench.generatedAt)}`
            : 'Loading operation evidence…'}
        </p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-200 dark:hover:bg-zinc-700"
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5', query.isFetching && 'animate-spin')}
            aria-hidden="true"
          />
          Refresh
        </button>
      </div>
      <section
        aria-label="PULSE filters"
        className="mb-4 rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800"
      >
        <div className="flex flex-wrap gap-2">
          <label className="relative min-w-52 flex-1">
            <span className="sr-only">Search operations</span>
            <Search
              className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400"
              aria-hidden="true"
            />
            <input
              aria-label="Search operations"
              value={search}
              onChange={(event) => setParam('search', event.target.value)}
              placeholder="Operation, source, owner, or description"
              className="h-9 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
            />
          </label>
          <SelectFilter
            label="Environment"
            value={environment || 'default'}
            values={['default', 'production', 'staging', 'ci', 'development', 'unknown']}
            onChange={(value) => setParam('environment', value, 'default')}
          />
          <SelectFilter
            label="Runtime"
            value={runtime}
            values={RUNTIMES}
            onChange={(value) => setParam('runtime', value, 'all')}
          />
          <SelectFilter
            label="Status"
            value={status}
            values={STATUSES}
            onChange={(value) => setParam('status', value, 'all')}
          />
          <SelectFilter
            label="Migration"
            value={migrationState}
            values={MIGRATION_STATES}
            onChange={(value) => setParam('migrationState', value, 'all')}
          />
          <input
            aria-label="Owner"
            value={owner}
            onChange={(event) => setParam('owner', event.target.value)}
            placeholder="Owner ID"
            className="h-9 w-28 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
          />
          <input
            aria-label="Operation family"
            value={family}
            onChange={(event) => setParam('family', event.target.value)}
            placeholder="Family"
            className="h-9 w-36 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
          />
        </div>
      </section>
      {query.isError ? (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          PULSE workbench request failed. No health conclusion can be drawn.
        </div>
      ) : query.data ? (
        <PulseOperationsContent
          payload={query.data.data}
          selectedOperationId={selectedOperationId}
          onSelectOperation={(operationId) => setParam('operation', operationId)}
        />
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-800">
          Loading PULSE registry and evidence…
        </div>
      )}
    </div>
  );
}
